/**
 * Maps TscnNode.type strings to React components. Types self-register, as with
 * NodeRegistry. Re-registering a typeName overwrites without a warning, for HMR.
 */
import { createTypeRegistry } from '../core/createTypeRegistry';
import { descendsFromClass } from '../godot/classdb/classBaseTypes';
import { withGeometryInstance } from './visibilityRange/geometryInstance';
import { isInvisibleBase } from './invisibleBase';
import { TWO_D_UI_TYPES } from './controls/has2DUIContent';
/**
 * The badge reads the declared intent, so it must agree with the component: a `pending` gap
 * mounts only an invisible base, and a `draws` type draws. The one exception is the 3D
 * pass-through (`container`) of a 2D-UI type, which its Control component draws.
 */
function assertIntentMatchesComponent({ typeName, Component, renderIntent = 'draws', container, }) {
    const mountsBase = isInvisibleBase(Component);
    if (renderIntent === 'pending' && !mountsBase) {
        throw new Error(`${typeName}: renderIntent 'pending' expects an invisible base, got a component that draws`);
    }
    if (renderIntent === 'draws' && mountsBase && !(container && TWO_D_UI_TYPES.has(typeName))) {
        throw new Error(`${typeName}: renderIntent 'draws' expects a component that draws, got an invisible base`);
    }
}
class NodeComponentRegistryImpl {
    // The whole registration is stored and answers every flag, so a new flag needs no
    // plumbing in `register` or `clear`.
    registry = createTypeRegistry('NodeComponentRegistry');
    /**
     * A GeometryInstance3D type's component is mounted inside the node's place in the scene cull,
     * drawn or not, so every such type takes part in the visibility range.
     */
    register(registration) {
        assertIntentMatchesComponent(registration);
        const { typeName, Component } = registration;
        const mounted = descendsFromClass(typeName, 'GeometryInstance3D')
            ? withGeometryInstance(Component)
            : Component;
        this.registry.register(typeName, { ...registration, Component: mounted });
    }
    get(typeName) {
        return this.registry.get(typeName)?.Component;
    }
    /** True when the type registered as CanvasItem (2D-canvas world content). */
    isCanvasItem(typeName) {
        return this.registry.get(typeName)?.canvasItem === true;
    }
    /** True when the type registered as a workspace-neutral container. */
    isContainer(typeName) {
        return this.registry.get(typeName)?.container === true;
    }
    /** True for an unregistered type or a container: the dispatcher mounts it in either workspace. */
    passesThrough(typeName) {
        const registration = this.registry.get(typeName);
        return registration === undefined || registration.container === true;
    }
    /** True when the type takes part in CSG boolean evaluation. */
    isCsgShape(typeName) {
        return this.registry.get(typeName)?.csgShape !== undefined;
    }
    /** The declared intent, or undefined when the type registers no component. */
    renderIntentOf(typeName) {
        const registration = this.registry.get(typeName);
        return registration === undefined ? undefined : (registration.renderIntent ?? 'draws');
    }
    /** The icon registration of a type, or undefined when Godot's editor draws no icon for it. */
    editorIconOf(typeName) {
        return this.registry.get(typeName)?.editorIcon;
    }
    /** The pose modifier reader of a type, or undefined when it is not a SkeletonModifier3D. */
    poseModifierOf(typeName) {
        return this.registry.get(typeName)?.poseModifier;
    }
    getYSortGroup(typeName) {
        return this.registry.get(typeName)?.ySortGroup;
    }
    /** The CSG registration for a type, or undefined when it is not a CSG shape. */
    getCsgShape(typeName) {
        return this.registry.get(typeName)?.csgShape;
    }
    getAllTypeNames() {
        return this.registry.getAllTypeNames();
    }
    /** Every type that registers a scene pass: each acts on nodes other than its own. */
    scenePassTypeNames() {
        return new Set(this.registry.getAllTypeNames().filter((typeName) => this.registry.get(typeName)?.scenePass));
    }
    clear() {
        this.registry.clear();
    }
}
export const nodeComponentRegistry = new NodeComponentRegistryImpl();
//# sourceMappingURL=NodeComponentRegistry.js.map